import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ConsentAnswerDto,
  ConsentService,
  ConsentTextDto,
} from '~js/React/services/generatedApi';

// Test data to pass in place of resp values
//  below for light mockup testing
//===========================================
const testConsent: ConsentTextDto = {
  text: '##### (DUMMY DATA CONSENT) Dela mina uppgifter mellan Länsförsäkringar fastighetsförmedling och övriga bolag inom LF-gruppen\n**Jag vill ha möjligheten att få bättre anpassade erbjudanden och rabatter, och samtycker därför till att mina uppgifter delas mellan Länsförsäkringar Fastighetsförmedling och övriga bolag i LF-gruppen.**\n\nSyftet med att dela dina uppgifter mellan bolagen är att kunna ge dig paketerbjudanden, personliga rabatter och rådgivning anpassad efter dina behov. Om du ger samtycke delas information om till exempel\n- dina avtalsengagemang hos LF\n- köp och försäljning av bostad (ej lånelöftesbelopp)\n- ditt deltagande i budgivningar (inte budgivningsnivå)\n- vilka sidor du visat intresse för på lansforsakringar.se och lansfast.se.\n\nDu kan när som helst återkalla ditt samtycke via dina inloggade kanaler på [lansforsakringar.se](https://www.lansforsakringar.se) eller [lansfast.se](https://www.lansfast.se). Läs mer om [LF:s behandling av personuppgifter](https://www.lansforsakringar.se) och [Fastighetsförmedlingens integritetspolicy](https://www.lansfast.se).',
  consentType: 'LFLFFInformationsharing',
  consentVersion: {
    major: 9,
    minor: 0,
    patch: 0,
  },
};

const testConsentAnswers: ConsentAnswerDto[] = [
  {
    status: 'Declined',
    text: '##### (DUMMY DATA ANSWER) Dela mina uppgifter mellan Länsförsäkringar fastighetsförmedling och övriga bolag inom LF-gruppen\n**Jag vill ha möjligheten att få bättre anpassade erbjudanden och rabatter, och samtycker därför till att mina uppgifter delas mellan Länsförsäkringar Fastighetsförmedling och övriga bolag i LF-gruppen.**\n\nSyftet med att dela dina uppgifter mellan bolagen är att kunna ge dig paketerbjudanden, personliga rabatter och rådgivning anpassad efter dina behov. Om du ger samtycke delas information om till exempel\n- dina avtalsengagemang hos LF\n- köp och försäljning av bostad (ej lånelöftesbelopp)\n- ditt deltagande i budgivningar (inte budgivningsnivå)\n- vilka sidor du visat intresse för på lansforsakringar.se och lansfast.se.\n\nDu kan när som helst återkalla ditt samtycke via dina inloggade kanaler på [lansforsakringar.se](https://www.lansforsakringar.se) eller [lansfast.se](https://www.lansfast.se). Läs mer om [LF:s behandling av personuppgifter](https://www.lansforsakringar.se) och [Fastighetsförmedlingens integritetspolicy](https://www.lansfast.se).',
    consentType: 'LFLFFInformationsharing',
    uuid: 'test-uuid-1',
    consentVersion: {
      major: 1,
      minor: 2,
      patch: 0,
    },
  },
  {
    status: 'Approved',
    text: '##### (DUMMY DATA ANSWER) Dela mina uppgifter mellan Länsförsäkringar fastighetsförmedling och övriga bolag inom LF-gruppen\n**Jag vill ha möjligheten att få bättre anpassade erbjudanden och rabatter, och samtycker därför till att mina uppgifter delas mellan Länsförsäkringar Fastighetsförmedling och övriga bolag i LF-gruppen.**\n\nSyftet med att dela dina uppgifter mellan bolagen är att kunna ge dig paketerbjudanden, personliga rabatter och rådgivning anpassad efter dina behov. Om du ger samtycke delas information om till exempel\n- dina avtalsengagemang hos LF\n- köp och försäljning av bostad (ej lånelöftesbelopp)\n- ditt deltagande i budgivningar (inte budgivningsnivå)\n- vilka sidor du visat intresse för på lansforsakringar.se och lansfast.se.\n\nDu kan när som helst återkalla ditt samtycke via dina inloggade kanaler på [lansforsakringar.se](https://www.lansforsakringar.se) eller [lansfast.se](https://www.lansfast.se). Läs mer om [LF:s behandling av personuppgifter](https://www.lansforsakringar.se) och [Fastighetsförmedlingens integritetspolicy](https://www.lansfast.se).',
    consentType: 'LFLFFInformationsharing',
    uuid: 'test-uuid-2',
    consentVersion: {
      major: 1,
      minor: 0,
      patch: 0,
    },
  },
];
//=========================================

const useConsent = (sessiondId: string) => {
  const [consent, setConsent] = useState<ConsentTextDto>(null);
  const [consentAnswers, setConsentAnswers] =
    useState<ConsentAnswerDto[]>(null);

  const isEmptyObject = (obj: Record<string, any>) =>
    Object.values(obj).every((value) => value === null || value === undefined);

  const fetchConsentsAndAnswers = useCallback(async () => {
    if (!sessiondId) return;

    // Consents (new consents)
    const consentResp = await ConsentService.consentGetConsents(sessiondId);

    // Consent Answers (prior consents)
    const consentAnswersResp = await ConsentService.consentGetConsentAnswer(
      sessiondId
    );

    // Update CONSENT state
    if (consentResp && !isEmptyObject(consentResp)) {
      setConsent(consentResp);
    } else {
      setConsent(null);
    }

    // Update CONSENT ANSWERS state
    if (consentAnswersResp && consentAnswersResp.length > 0) {
      setConsentAnswers(consentAnswersResp);
    } else {
      setConsentAnswers(null);
    }

    return;
  }, [sessiondId]);

  useEffect(() => {
    if (sessiondId) {
      fetchConsentsAndAnswers();
    }
  }, [sessiondId, fetchConsentsAndAnswers]);

  return {
    consent,
    consentAnswers,
    refetch: fetchConsentsAndAnswers,
  };
};

export default useConsent;
